import { computed, onScopeDispose, ref, watch, Ref } from 'vue'
import type { CatalogProduct, CatalogProductPage } from '../services/MetaService/types'

export interface CatalogThumbnailApi {
  getCatalogProducts(wabaId: string, catalogId: string, after?: string, retailerId?: string): Promise<CatalogProductPage>
}

export function useCatalogThumbnail (
  wabaId: Ref<string>, catalogId: Ref<string>, requestedSku: Ref<string>, getApi: () => Promise<CatalogThumbnailApi>
) {
  const products = ref<CatalogProduct[]>([])
  const nextCursor = ref<string | null>(null)
  const loading = ref(false)
  const loadingMore = ref(false)
  const requestError = ref('')
  const loadMoreError = ref('')
  const product = computed(() => requestedSku.value.trim()
    ? products.value.find(item => item.retailer_id === requestedSku.value.trim())
    : products.value[0])
  const error = computed(() => {
    if (requestError.value) return requestError.value
    if (loading.value || !wabaId.value || !catalogId.value || product.value) return ''
    return requestedSku.value.trim() ? 'No product matches this SKU in the connected catalog.' : 'No product with a SKU is available to preview.'
  })
  let generation = 0
  let disposed = false

  async function load (append = false) {
    if (disposed || (append && (loading.value || loadingMore.value || !nextCursor.value))) return
    const request = append ? generation : ++generation
    const waba = wabaId.value
    const catalog = catalogId.value
    const after = append ? nextCursor.value || undefined : undefined
    const selectedSku = requestedSku.value.trim()
    if (append) {
      loadingMore.value = true
      loadMoreError.value = ''
    } else {
      products.value = []
      nextCursor.value = null
      requestError.value = ''
      loadMoreError.value = ''
      loadingMore.value = false
      loading.value = Boolean(waba && catalog)
    }
    if (!waba || !catalog) return
    try {
      const api = await getApi()
      if (disposed || request !== generation) return
      const page = await api.getCatalogProducts(waba, catalog, after)
      if (disposed || request !== generation) return
      const all = append ? [...products.value, ...page.data] : [...page.data]
      if (!append && selectedSku && !all.some(item => item.retailer_id === selectedSku)) {
        const selectedPage = await api.getCatalogProducts(waba, catalog, undefined, selectedSku)
        if (disposed || request !== generation) return
        const selected = selectedPage.data.find(item => item.retailer_id === selectedSku)
        if (selected) all.push(selected)
      }
      products.value = [...new Map(all.filter(item => item.retailer_id?.trim()).map(item => [item.retailer_id, item])).values()]
      nextCursor.value = page.next_cursor || null
    } catch (e) {
      if (!disposed && request === generation) {
        const message = e instanceof Error ? e.message : String(e)
        if (append) loadMoreError.value = message
        else requestError.value = message
      }
    } finally {
      if (!disposed && request === generation) {
        if (append) loadingMore.value = false
        else loading.value = false
      }
    }
  }

  watch([wabaId, catalogId], () => { requestedSku.value = ''; load() }, { immediate: true, flush: 'sync' })
  onScopeDispose(() => { disposed = true; ++generation })
  return { product, products, nextCursor, loading, loadingMore, error, loadMoreError, refresh: () => load(), loadMore: () => load(true) }
}
